import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Heading, HStack, Text, VStack } from "native-base";
import { Helmet } from "react-helmet-async";
import { State, City } from "country-state-city";
import styles from "./styles.module.css";
import Stepper from "../../../../components/stepper";
import colors from "../../../../constants/colors";
import { StateContext, StatusContext } from "../../../../contexts";
import API from "../../../../api";
import { useNavigate } from "react-router-dom";
import routes from "../../../../routes";

function SignUpAgencyDataPage() {
  const navigate = useNavigate();
  const stateContext = useContext(StateContext);
  const status = useContext(StatusContext);
  const [name, setName] = useState("");
  const [managerFirstName, setManagerFirstName] = useState("");
  const [managerLastName, setManagerLastName] = useState("");
  const [country, setCountry] = useState("MX");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [localPhone, setLocalPhone] = useState("");
  const [phoneExtension, setPhoneExtension] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setManagerFirstName(stateContext.user.firstName);
    setManagerLastName(stateContext.user.lastName);
  }, [stateContext.user]);

  const onSave = async () => {
    try {
      setIsLoading(true);
      await API(stateContext.jwt).put(
        `/agencies/${stateContext.user.agency.id}`,
        {
          data: {
            name,
            managerFirstName,
            managerLastName,
            country: "México",
            state: State.getStateByCodeAndCountry(state, country).name,
            city,
            postalCode,
            address,
            phone,
            localPhone,
            phoneExtension,
          },
        },
      );
      navigate(routes.signUpAgencyDocuments);
    } catch (error) {
      setIsLoading(false);
      status.updateErrorMessage(error);
    }
  };

  const states = useMemo(() => State.getStatesOfCountry(country), [country]);

  const cities = useMemo(
    () => City.getCitiesOfState(country, state),
    [country, state],
  );

  return (
    <VStack paddingTop={50} alignItems="center">
      <Helmet>
        <title>Registro - Datos de tu empresa</title>
      </Helmet>
      <Heading color={colors.secondary} fontSize="2xl" textAlign="center">
        Registrarse
      </Heading>
      <Stepper step={3} />
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}>
        <VStack
          backgroundColor={colors.white}
          padding={10}
          borderRadius={5}
          space={5}>
          <Heading color={colors.secondary} fontSize="xl">
            Datos de tu empresa
          </Heading>
          <HStack marginTop={5} space={5}>
            <label className={styles.input}>
              Nombre comercial
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
          </HStack>
          <HStack marginTop={5} space={5}>
            <label className={styles.input}>
              Nombre del adminstrador
              <input
                type="text"
                value={managerFirstName}
                onChange={(e) => setManagerFirstName(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
            <label className={styles.input}>
              Apellido del administrador
              <input
                type="text"
                value={managerLastName}
                onChange={(e) => setManagerLastName(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
          </HStack>
          <HStack marginTop={5} space={5}>
            <label className={styles.input}>
              País
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
                disabled={isLoading}>
                {/* NOTE Descomentar y eliminar el option siguiente para habilitar todos los paises
                {Country.getAllCountries().map((_country) => (
                  <option key={_country.isoCode} value={_country.isoCode}>
                    {_country.name}
                  </option>
                ))} */}
                <option value="MX">México</option>
              </select>
            </label>
            <label className={styles.input}>
              Estado
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
                disabled={isLoading}>
                <option value="" disabled></option>
                {states.map((_state) => (
                  <option key={_state.isoCode} value={_state.isoCode}>
                    {_state.name}
                  </option>
                ))}
              </select>
            </label>
          </HStack>
          <HStack marginTop={5} space={5}>
            <label className={styles.input}>
              Municipio
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
                disabled={isLoading}>
                <option value="" disabled></option>
                {cities.map((_city) => (
                  <option
                    key={_city.latitude + _city.longitude}
                    value={_city.name}>
                    {_city.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.input}>
              CP
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
          </HStack>
          <HStack marginTop={5} space={5}>
            <label className={styles.input}>
              Dirección
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
            <label className={styles.input}>
              Teléfono móvil
              <input
                type="tel"
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/[^0-9]/g, ""))
                }
                required
                disabled={isLoading}
              />
            </label>
          </HStack>
          <HStack marginTop={5} space={5}>
            <label className={styles.input}>
              Teléfono local
              <input
                type="tel"
                value={localPhone}
                onChange={(e) =>
                  setLocalPhone(e.target.value.replace(/[^0-9]/g, ""))
                }
                required
                disabled={isLoading}
              />
            </label>
            <label className={styles.input}>
              Extensión (opcional)
              <input
                type="tel"
                value={phoneExtension}
                onChange={(e) =>
                  setPhoneExtension(e.target.value.replace(/[^0-9]/g, ""))
                }
                disabled={isLoading}
              />
            </label>
          </HStack>
        </VStack>
        <button disabled={isLoading}>
          <Box
            backgroundColor={colors.primary}
            paddingX={20}
            paddingY={2}
            borderRadius="full">
            <Text color={colors.white}>Continuar</Text>
          </Box>
        </button>
      </form>
    </VStack>
  );
}

export default SignUpAgencyDataPage;
